import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "./ManageOrganizersPage.css"; // Crear este archivo CSS

function ManageOrganizersPage() {
  const [organizers, setOrganizers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(""); // Mensajes de éxito
  const [showErrorPopup, setShowErrorPopup] = useState(false); // Estado para pop-up de error
  const [errorPopupMessage, setErrorPopupMessage] = useState(""); // Mensaje de pop-up de error

  // Estados para Paginación (si se necesita para muchos organizadores)
  const [currentPage, setCurrentPage] = useState(1);
  const [organizersPerPage] = useState(10); // Mostrar 10 organizadores por página

  // Estados para Búsqueda y Filtro
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("name"); // Buscar por nombre de organizador

  const { token } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // Función para obtener todos los organizadores del backend
  const fetchOrganizers = async () => {
    setIsLoading(true);
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    try {
      const response = await axios.get(`${API_BASE_URL}/organizers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOrganizers(response.data);
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al cargar los organizadores."
      );
      setShowErrorPopup(true);
      console.error("Error fetching organizers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar los organizadores al montar el componente o cuando el token cambia
  useEffect(() => {
    if (token) {
      fetchOrganizers();
    } else {
      setErrorPopupMessage("No autenticado. Por favor, inicie sesión.");
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  }, [token, API_BASE_URL]);

  // Lógica de Filtrado en Tiempo Real
  const filteredOrganizers = useMemo(() => {
    if (!searchTerm) {
      return organizers;
    }
    return organizers.filter((organizer) => {
      const value = String(organizer[searchColumn]).toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    });
  }, [organizers, searchTerm, searchColumn]);

  // Lógica de Paginación
  const indexOfLastOrganizer = currentPage * organizersPerPage;
  const indexOfFirstOrganizer = indexOfLastOrganizer - organizersPerPage;
  const currentOrganizers = filteredOrganizers.slice(
    indexOfFirstOrganizer,
    indexOfLastOrganizer
  );
  const totalPages = Math.ceil(filteredOrganizers.length / organizersPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Función para editar un organizador (por ahora, placeholder)
  const handleEditOrganizer = (organizerId) => {
    // Implementaremos la navegación a una página de edición más adelante
    navigate(`/admin/organizers/edit/${organizerId}`);
  };

  // Función para eliminar un organizador
  const handleDeleteOrganizer = async (organizerId) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar este organizador? Esta acción no se puede deshacer y puede afectar eventos asociados."
      )
    ) {
      return;
    }
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    setIsLoading(true);

    try {
      await axios.delete(`${API_BASE_URL}/organizers/${organizerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Organizador eliminado exitosamente.");
      fetchOrganizers(); // Recargar la lista
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al eliminar el organizador."
      );
      setShowErrorPopup(true);
      console.error("Error deleting organizer:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar el pop-up de error
  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorPopupMessage("");
  };

  if (isLoading) {
    return (
      <div className="manage-organizers-container">
        <p>Cargando organizadores...</p>
      </div>
    );
  }

  return (
    <div className="manage-organizers-container">
      <div className="manage-organizers-card">
        <h2 className="manage-organizers-title">Gestión de Organizadores</h2>
        <p className="manage-organizers-description">
          Administra las instituciones y entidades que organizan los eventos.
        </p>

        <div className="top-controls">
          <Link
            to="/admin/organizers/create"
            className="create-organizer-button"
          >
            <span className="material-icons">add</span> Crear Nuevo Organizador
          </Link>
          <div className="search-filter-container">
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
              className="search-column-select"
            >
              <option value="name">Nombre del Organizador</option>
              {/* Puedes añadir más columnas para búsqueda si el backend las retorna */}
            </select>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
        </div>

        {message && (
          <p className="manage-organizers-message success">{message}</p>
        )}

        {filteredOrganizers.length === 0 && searchTerm !== "" ? (
          <p className="no-results-message">
            No se encontraron organizadores que coincidan con la búsqueda.
          </p>
        ) : filteredOrganizers.length === 0 ? (
          <p className="no-results-message">
            No hay organizadores registrados.{" "}
            <Link to="/admin/organizers/create">Crea uno ahora</Link>.
          </p>
        ) : (
          <table className="organizers-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre del Organizador</th>
                <th>Logo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentOrganizers.map((organizer) => (
                <tr key={organizer.id}>
                  <td>{organizer.id}</td>
                  <td>{organizer.name}</td>
                  <td>
                    {organizer.url_logo ? (
                      <img
                        src={`${API_BASE_URL}/uploads/${organizer.url_logo}`}
                        alt={`Logo de ${organizer.name}`}
                        className="organizer-logo-thumbnail"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://placehold.co/50x50/cccccc/333333?text=Logo";
                        }}
                      />
                    ) : (
                      <span>Sin logo</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons-container">
                      <button
                        onClick={() => handleEditOrganizer(organizer.id)}
                        className="action-button edit-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">edit</span>
                        <span className="button-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDeleteOrganizer(organizer.id)}
                        className="action-button delete-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">delete</span>
                        <span className="button-text">Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Controles de Paginación */}
        {filteredOrganizers.length > organizersPerPage && (
          <div className="pagination-controls">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                className={`pagination-button ${
                  currentPage === i + 1 ? "active" : ""
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Pop-up de Error (similar al de eventos) */}
      {showErrorPopup && (
        <div className="popup-overlay">
          <div className="popup-content error-popup">
            <h3>¡Error!</h3>
            <p>{errorPopupMessage}</p>
            <button
              onClick={handleCloseErrorPopup}
              className="popup-button error-button"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageOrganizersPage;
